<script>
import { createMember } from '@/api/member.js'

export default {
  name: 'MemberCreateDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'created'],
  data() {
    return {
      form: this.getInitialForm(),
      formValid: false,
      loading: false,
      errorMsg: '',
      showPassword: false,
      showPasswordConfirm: false,
    }
  },
  computed: {
    dialogVisible: {
      get() { return this.modelValue },
      set(val) { this.$emit('update:modelValue', val) },
    },
  },
  watch: {
    modelValue(val) {
      if (val) this.resetForm()
    },
  },
  methods: {
    getInitialForm() {
      return {
        name: '',
        email: '',
        password: '',
        passwordConfirm: '',
        phone: '',
        birthDate: '',
        zipCode: '',
        address1: '',
        address2: '',
      }
    },
    resetForm() {
      this.form = this.getInitialForm()
      this.errorMsg = ''
      this.loading = false
      this.showPassword = false
      this.showPasswordConfirm = false
    },
    closeDialog() {
      this.dialogVisible = false
    },
    async handleSubmit() {
      const { valid } = await this.$refs.form.validate()
      if (!valid) return

      this.loading = true
      this.errorMsg = ''

      try {
        const res = await createMember({
          name: this.form.name.trim(),
          email: this.form.email.trim(),
          password: this.form.password,
          phone: this.form.phone || undefined,
          birthDate: this.form.birthDate || undefined,
          zipCode: this.form.zipCode || undefined,
          address1: this.form.address1 || undefined,
          address2: this.form.address2 || undefined,
        })

        if (res.data?.code === 0) {
          this.$emit('created')
        } else {
          this.errorMsg = res.data?.message || '회원 등록에 실패했습니다.'
        }
      } catch (e) {
        console.error('[MemberCreateDialog] createMember error:', e)
        this.errorMsg = e?.response?.data?.message || '서버 오류가 발생했습니다.'
      } finally {
        this.loading = false
      }
    },
    searchAddress() {
      if (!window.daum?.Postcode) {
        this.errorMsg = '주소 검색 스크립트를 불러오지 못했습니다.'
        return
      }
      new window.daum.Postcode({
        oncomplete: (data) => {
          this.form.zipCode = data.zonecode || ''
          this.form.address1 = data.roadAddress || data.autoRoadAddress || data.jibunAddress || ''
        },
      }).open()
    },
    openBirthDatePicker() {
      const input = this.$refs.birthDateInput?.$el?.querySelector('input')
      if (!input) return
      if (typeof input.showPicker === 'function') {
        input.showPicker()
        return
      }
      input.focus()
      input.click()
    },
    requiredRule(label) {
      return [
        v => !!String(v || '').trim() || `${label}은(는) 필수입니다.`,
      ]
    },
    emailRules() {
      return [
        v => !!String(v || '').trim() || '이메일은 필수입니다.',
        v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || '') || '이메일 형식을 확인해 주세요.',
      ]
    },
    passwordRules() {
      return [
        v => !!String(v || '') || '비밀번호는 필수입니다.',
        v => String(v || '').length >= 8 || '비밀번호는 8자 이상 입력해 주세요.',
      ]
    },
    passwordConfirmRules() {
      return [
        v => !!String(v || '') || '비밀번호 확인은 필수입니다.',
        v => v === this.form.password || '비밀번호가 일치하지 않습니다.',
      ]
    },
    phoneRules() {
      return [
        v => !v || /^[0-9-]{0,20}$/.test(v) || '연락처 형식이 올바르지 않습니다.',
      ]
    },
  },
}
</script>

<template>
  <v-dialog v-model="dialogVisible" max-width="720" persistent>
    <v-card rounded="lg">
      <v-card-title class="text-subtitle-1 font-weight-bold pa-5 pb-3 d-flex align-center">
        <v-icon color="primary" class="mr-2">mdi-account-plus-outline</v-icon>
        회원 등록
      </v-card-title>
      <v-divider />

      <v-card-text class="pa-5">
        <v-form ref="form" v-model="formValid" @submit.prevent="handleSubmit">
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.name"
                label="회원 이름"
                placeholder="회원 이름을 입력하세요"
                variant="outlined"
                density="comfortable"
                :rules="requiredRule('회원 이름')"
                autofocus
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.email"
                label="이메일"
                placeholder="example@goldnara.co.kr"
                variant="outlined"
                density="comfortable"
                :rules="emailRules()"
                prepend-inner-icon="mdi-email-outline"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.password"
                label="초기 비밀번호"
                placeholder="8자 이상 입력"
                variant="outlined"
                density="comfortable"
                :type="showPassword ? 'text' : 'password'"
                :append-inner-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                :rules="passwordRules()"
                autocomplete="new-password"
                @click:append-inner="showPassword = !showPassword"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.passwordConfirm"
                label="초기 비밀번호 확인"
                placeholder="비밀번호를 다시 입력"
                variant="outlined"
                density="comfortable"
                :type="showPasswordConfirm ? 'text' : 'password'"
                :append-inner-icon="showPasswordConfirm ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                :rules="passwordConfirmRules()"
                autocomplete="new-password"
                @click:append-inner="showPasswordConfirm = !showPasswordConfirm"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.phone"
                label="연락처"
                placeholder="01012345678"
                variant="outlined"
                density="comfortable"
                :rules="phoneRules()"
                prepend-inner-icon="mdi-phone-outline"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                ref="birthDateInput"
                v-model="form.birthDate"
                label="생년월일"
                type="date"
                variant="outlined"
                density="comfortable"
                append-inner-icon="mdi-calendar-month-outline"
                class="member-date-field"
                @click:append-inner="openBirthDatePicker"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="form.zipCode"
                label="우편번호"
                variant="outlined"
                density="comfortable"
                readonly
              />
            </v-col>
            <v-col cols="12" md="8" class="d-flex align-start ga-2">
              <v-text-field
                v-model="form.address1"
                label="주소"
                variant="outlined"
                density="comfortable"
                readonly
                class="flex-grow-1"
              />
              <v-btn height="48" variant="tonal" color="primary" prepend-icon="mdi-map-search-outline" @click="searchAddress">
                주소 찾기
              </v-btn>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="form.address2"
                label="상세 주소"
                variant="outlined"
                density="comfortable"
              />
            </v-col>
          </v-row>
        </v-form>

        <v-alert v-if="errorMsg" type="error" variant="tonal" density="compact" class="mt-3">
          {{ errorMsg }}
        </v-alert>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn variant="outlined" :disabled="loading" @click="closeDialog">취소</v-btn>
        <v-btn color="primary" :loading="loading" prepend-icon="mdi-check" @click="handleSubmit">
          등록
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.member-date-field :deep(input[type="date"]::-webkit-calendar-picker-indicator) {
  opacity: 0;
}

.member-date-field :deep(.v-field__append-inner) {
  color: #cdb27a;
  cursor: pointer;
}
</style>
